/**
 * DynamicSelectField — select whose options come from another field's
 * selected-model metadata (backend `ConfigDynamicSelect`). NOT related to
 * lucide-react's `DynamicIcon` (which is forbidden in this codebase): no
 * code is lazy-loaded by string name here.
 *
 * Used for the embedding-dimension picker, which reads
 * `supported_dimensions` off the selected embedding model on
 * `/models/embeddings`. Hides itself when the resolved model has no
 * options and no fallback.
 */

import { useMemo } from "react";
import { useModels } from "@/hooks/useModels";
import {
  Controller,
  useWatch,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";

import { OptionType, SpotSelect } from "@/components/Form/SelectField";
import { type ComboboxOption } from "@/components/shadcn/combobox";

// Narrow shape of the dynamic payload we read off the selected model.
// Each `/models/*` row can carry arbitrary attributes — the config
// picker decides which one to surface via `sourceAttribute`. We only
// touch one attribute per render, so a dict of unknown values with a
// string key is the honest signature.
type ModelRow = { name: string } & Readonly<
  Record<string, ReadonlyArray<string | number> | null | undefined | unknown>
>;

interface IDynamicSelectFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  sourceField: Path<T>;
  sourceAttribute: string;
  endpoint: string;
  fallbackValues?: Array<string | number | OptionType>;
  label?: string;
  helperText?: string;
  placeholder?: string;
  tooltip?: string;
  isClearable?: boolean;
  isNotSearchable?: boolean;
  isDisabled?: boolean;
  isOptional?: boolean;
  hasPortal?: boolean;
}

function toOptions(
  raw: Array<string | number | OptionType> | undefined | null
): OptionType[] {
  if (!raw?.length) return [];
  return raw.map((v) =>
    typeof v === "string" || typeof v === "number"
      ? { value: String(v), label: String(v) }
      : v
  );
}

// No leading check icon — the selected value is already visible in the
// trigger, so the check column is dead horizontal space.
const renderPlainOption = (opt?: ComboboxOption) =>
  opt ? <span className="truncate">{opt.label}</span> : null;

export const DynamicSelectField = <T extends FieldValues>({
  name,
  control,
  sourceField,
  sourceAttribute,
  endpoint,
  fallbackValues,
  label,
  helperText,
  placeholder,
  tooltip,
  isClearable,
  isNotSearchable,
  isDisabled,
  isOptional,
  hasPortal,
}: IDynamicSelectFieldProps<T>) => {
  const rawSourceValue = useWatch({ control, name: sourceField });
  const sourceValue = Array.isArray(rawSourceValue)
    ? rawSourceValue[0]
    : rawSourceValue;

  const { data: models, isLoading } = useModels(endpoint, Boolean(sourceValue));

  const options = useMemo<OptionType[]>(() => {
    if (!sourceValue || !models) return toOptions(fallbackValues);
    const match = (models as ReadonlyArray<ModelRow>).find(
      (m) => m.name === sourceValue
    );
    const raw = match?.[sourceAttribute];
    const derived =
      Array.isArray(raw) &&
      raw.every((v) => typeof v === "string" || typeof v === "number")
        ? (raw as Array<string | number>)
        : undefined;
    const fromSource = toOptions(derived);
    return fromSource.length > 0 ? fromSource : toOptions(fallbackValues);
  }, [sourceValue, models, sourceAttribute, fallbackValues]);

  // Platform-wide preferred dim (keep in sync with PREFERRED_DEFAULT_DIM in
  // spotflow/forms/kb.py). When the selected model supports it, display it
  // pre-selected in the trigger so users aren't staring at a placeholder —
  // but don't write it to form state; null ingests as the same dim anyway.
  const displayDefault = options.some((o) => o.value === "768")
    ? "768"
    : undefined;

  if (options.length === 0) return null;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <SpotSelect
          name={name}
          label={label}
          tooltip={tooltip}
          helperText={helperText}
          placeholder={placeholder}
          options={options}
          // Backend stores the dim as an int; JSON round-trips it as a
          // JS number. Combobox compares option.value with strict-equal
          // against a string key, so coerce here or the saved value
          // won't match any option after reload. When the form value is
          // null, fall back to the platform-preferred default so the
          // trigger shows a concrete number instead of the placeholder.
          value={field.value == null ? displayDefault : String(field.value)}
          onChange={(opt: OptionType | null) =>
            field.onChange(opt?.value ?? null)
          }
          isDisabled={isDisabled}
          isLoading={isLoading}
          isClearable={isClearable}
          isNotSearchable={isNotSearchable}
          isOptional={isOptional}
          hasPortal={hasPortal}
          customItemContent={renderPlainOption}
        />
      )}
    />
  );
};
